"use client";

import { CTA_CLICKED_VALUES, STATSIG_EVENTS } from "@repo/statsig";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { useButtonPendingAction } from "src/lib/use-button-pending-action";
import BrandedActionButton from "src/components/branded-action-button";
import { BodyText, HeadlineText } from "src/components/KinsaTypography";
import { Patient } from "src/lib/definitions";
import { Routes } from "src/lib/routes";
import { Drawer } from "vaul";
import { checkForActiveVisit } from "../actions";
import { usePatientCaseStatus } from "../hooks/usePatientCaseStatus";
import PatientSelector from "./PatientSelector";

interface PatientSelectionContentProps {
  patients: Patient[];
  initialSelectedPatient?: Patient;
  onConfirm: (patient: Patient) => void;
  onCancel: () => void;
  onBlockVisit?: (patient: Patient) => void;
  layoutId?: string;
}

const PatientSelectionContent: React.FC<PatientSelectionContentProps> = ({
  patients,
  initialSelectedPatient,
  onConfirm,
  onCancel,
  onBlockVisit,
}) => {
  const router = useRouter();
  const [selectedPatient, setSelectedPatient] = useState(
    initialSelectedPatient || patients[0]
  );
  const { isPending: isConfirming, startAction: startConfirmAction } =
    useButtonPendingAction();
  const [isCancelling, setIsCancelling] = useState(false);

  const { patientCaseStatus, nextRoute, isCaseStatusLoading, visitsLoading } =
    usePatientCaseStatus(selectedPatient);

  const navigateToBlockVisit = (patient: Patient) => {
    if (onBlockVisit) {
      onBlockVisit(patient);
      return;
    }
    const params = new URLSearchParams({
      id: patient.id,
      firstName: patient.firstName,
      ...(patient.custom?.profileImage && {
        profileImage: patient.custom.profileImage,
      }),
    });
    router.push(`${Routes.BLOCK_VISIT}?${params.toString()}`);
  };

  const handleConfirm = () => {
    startConfirmAction(async () => {
      // First check if the profile is being created for a patient with an active visit
      if (patientCaseStatus === null) {
        // Check for active visits before allowing a new visit request
        const hasActiveVisit = await checkForActiveVisit(selectedPatient);

        if (hasActiveVisit) {
          navigateToBlockVisit(selectedPatient);
          return;
        }

        onConfirm(selectedPatient);
      } else if (patientCaseStatus === "requested") {
        // Patient already has a requested visit, navigate to block visit page
        navigateToBlockVisit(selectedPatient);
      } else if (patientCaseStatus === "completed") {
        //TODO: Visit summary?
        //router.push(nextRoute)
        onConfirm(selectedPatient);
      } else if (patientCaseStatus === "active" && nextRoute) {
        // Patient already has an active visit, navigate to block visit page
        navigateToBlockVisit(selectedPatient);
      } else {
        onConfirm(selectedPatient);
      }
    });
  };

  const handleCancel = () => {
    setIsCancelling(true);
    onCancel();
  };

  const isConfirmDisabled =
    !selectedPatient ||
    isCaseStatusLoading ||
    visitsLoading ||
    isConfirming ||
    isCancelling;

  const isCancelDisabled = isConfirming || isCancelling;

  return (
    <div className="flex flex-col">
      <Drawer.Title className="sr-only">Who is this visit for?</Drawer.Title>
      <Drawer.Description className="sr-only">
        Select the person who needs the visit
      </Drawer.Description>

      <div className="px-[24px] pt-[24px] pb-0">
        <div className="flex flex-col items-center gap-[32px]">
          <div className="flex flex-col items-center gap-1">
            <HeadlineText>Who is this visit for?</HeadlineText>
            <BodyText>Select the person who needs the visit</BodyText>
          </div>
          <PatientSelector
            patients={patients}
            selectedPatient={selectedPatient}
            onSelectPatient={(patient) => setSelectedPatient(patient)}
            className="w-full"
          />
        </div>
      </div>

      <div className="flex flex-col gap-2 px-[24px] pb-[24px] pt-[22px]">
        <BrandedActionButton
          onPress={handleConfirm}
          text={`REQUEST VISIT FOR ${selectedPatient.firstName.toUpperCase()}`}
          eventData={{
            action: CTA_CLICKED_VALUES.START_VISIT,
            buttonText: `REQUEST VISIT FOR ${selectedPatient.firstName.toUpperCase()}`,
          }}
          className="w-full"
          disabled={isConfirmDisabled}
          loading={isConfirming}
        />
        <BrandedActionButton
          onPress={handleCancel}
          text="CANCEL REQUEST"
          eventData={{
            action: CTA_CLICKED_VALUES.CANCEL_VISIT_REQUEST,
            buttonText: "CANCEL REQUEST",
            patient_id: selectedPatient.id,
            patient_name: selectedPatient.firstName,
            patient_count: patients.length.toString(),
          }}
          customEventName={STATSIG_EVENTS.VISIT_REQUEST_CANCELLED}
          variant="tertiary"
          className="m-0 w-full"
          disabled={isCancelDisabled}
          loading={isCancelling}
        />
      </div>
    </div>
  );
};

export default PatientSelectionContent;
